/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { ReactElement, useEffect, useRef } from 'react';
import { AdvancedMarker, APIProvider, Map, useMap } from '@vis.gl/react-google-maps';

export type TrackPoint = {
  lat: number;
  lng: number;
  t: number;
  /** Value to colour this point by, when colouring is on. */
  v?: number | null;
};
export type Track = { key: string; label: string; color: string; points: TrackPoint[] };

/** One band of a threshold configuration, for the legend. */
export type ThresholdBand = { label: string; color: string; detail: string };

/**
 * How to colour the path. `null` means one flat colour per track.
 *
 * TWO MODES, because they answer different questions.
 *
 * `threshold` compares each reading against the client's configured thresholds
 * for that parameter and paints it green, amber or red exactly as the rest of
 * the dashboard does. This is the mode to use when the question is "was the air
 * acceptable where I walked" — the colours mean the same thing here as on a
 * parameter card, so there is nothing to re-learn.
 *
 * `ramp` spreads a perceptual colour scale across the values actually present.
 * Use it for parameters with no configured threshold (a raw count, altitude)
 * where absolute judgement is impossible and only relative variation can be
 * shown. `lo` and `hi` come from the caller rather than being derived here, so
 * the map and the legend beside it agree, and so switching window does not
 * silently rescale the colours under the reader.
 */
export type TrackColoring =
  | {
      mode: 'threshold';
      key: string;
      label: string;
      unit: string;
      /** Threshold colour for one reading. */
      colorOf: (value: number) => string;
      bands: ThresholdBand[];
      /** Name of the threshold configuration in force, so the legend can say. */
      configName: string;
    }
  | { mode: 'ramp'; key: string; label: string; unit: string; lo: number; hi: number }
  | null;

/** One choice in the map's own parameter picker. */
export type ColorOption = { key: string; label: string };

type Props = {
  tracks: Track[];
  version: number;
  colorBy?: TrackColoring;
  /**
   * Parameters the track can be coloured by. Supplied with labels rather than
   * raw keys so this component needs no knowledge of the BLE key vocabulary.
   */
  colorOptions?: ColorOption[];
  /** The currently chosen key; '' means a flat line. */
  colorKey?: string;
  /**
   * Set to render the picker IN THIS CARD. The choice only ever affects this
   * map, so putting it anywhere else means scrolling away from the thing it
   * changes to change it — the same reason the range selector is repeated
   * above each group of graphs.
   */
  onColorChange?: (key: string) => void;
};

/**
 * Perceptually-ordered ramp, low to high — same family as the channel heat
 * map so the two read consistently. Monotonic in lightness, which is what
 * makes it survive greyscale printing and colour-blind viewers.
 */
const RAMP = [
  '#2b6ca8',
  '#2688b0',
  '#2fa3a8',
  '#5cbd91',
  '#9ed271',
  '#d7e159',
  '#fdd835',
  '#fb8c00',
  '#f4511e',
  '#d32f2f'
];

/** Bucket index for a value on the scale, or -1 when there is no value. */
function bucketOf(v: number | null | undefined, lo: number, hi: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return -1;
  if (hi <= lo) return 0;
  const norm = Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
  return Math.min(RAMP.length - 1, Math.floor(norm * RAMP.length));
}

/**
 * The colour one point should be drawn in, and a stable key for grouping
 * neighbouring points that share it.
 *
 * Runs are grouped by the COLOUR, not by a bucket index: in threshold mode
 * there is no ordered bucket to compare, only "which band did this land in",
 * and two different bands can legitimately share a colour (the configuration
 * uses one red for both the lower and upper critical side).
 */
function colorAt(point: TrackPoint, colorBy: TrackColoring, fallback: string): string | null {
  if (!colorBy) return fallback;
  const {v} = point;
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  if (colorBy.mode === 'threshold') return colorBy.colorOf(v);
  const bucket = bucketOf(v, colorBy.lo, colorBy.hi);
  return bucket >= 0 ? RAMP[bucket] : null;
}

/** The last point of a track that carries a reading, for the "latest" marker. */
function lastWithValue(points: TrackPoint[]): TrackPoint | null {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const {v} = points[i];
    if (typeof v === 'number' && Number.isFinite(v)) return points[i];
  }
  return null;
}

/** What the colours mean, in a few words, next to the picker. */
function scaleNote(colorBy: TrackColoring | undefined): string {
  if (!colorBy) return 'GPS track in window';
  if (colorBy.mode === 'threshold') return `${colorBy.configName} thresholds`;
  return 'relative scale';
}

/** Compact number for the colour-scale ends and the value pill. */
function fmtScale(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toFixed(0);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

/**
 * Draws each phone's GPS path as a polyline. Polylines are created once per
 * track and only have their path replaced on update — no React re-mounts of
 * map overlays at stream rates. The view auto-fits until the user pans or
 * zooms, then stays where they put it.
 *
 * COLOURING: a Google Maps polyline has ONE stroke colour, so a path coloured
 * by concentration has to be several polylines. Drawing one per point would
 * mean ~900 overlays for a 15-minute window at 1 Hz, which the map cannot
 * animate. Instead consecutive points that resolve to the same colour are
 * merged into a single run, so a typical track becomes a few dozen polylines —
 * and each run repeats its boundary point so the segments join with no gaps.
 */
function Tracks({ tracks, version, colorBy }: Pick<Props, 'tracks' | 'version' | 'colorBy'>): ReactElement | null {
  const map = useMap();
  const lines = useRef<Record<string, google.maps.Polyline>>({});
  const userMoved = useRef(false);
  const fitting = useRef(false);

  useEffect(() => {
    if (!map) return undefined;
    const listener = map.addListener('dragstart', () => {
      userMoved.current = true;
    });
    const zoom = map.addListener('zoom_changed', () => {
      if (!fitting.current) userMoved.current = true;
    });
    return () => {
      listener.remove();
      zoom.remove();
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    // Each track becomes one or more polylines, keyed "<track>#<run>". Build
    // the full set of keys this render needs, then drop every overlay not in
    // it — that one pass handles tracks disappearing AND a track needing fewer
    // runs than last time, which would otherwise leave stale colour segments
    // drawn over the new path.
    //
    // A plain object, not a Map: `Map` in this module is the react-google-maps
    // component, so the global is shadowed.
    const wanted: Record<string, { path: google.maps.LatLngLiteral[]; color: string }> = {};

    tracks.forEach((t) => {
      const pts = t.points;
      if (pts.length === 0) return;

      if (!colorBy) {
        wanted[`${t.key}#0`] = {
          path: pts.map((p) => ({ lat: p.lat, lng: p.lng })),
          color: t.color
        };
        return;
      }

      let run: google.maps.LatLngLiteral[] = [];
      let runColor: string | null = null;
      let started = false;
      let index = 0;
      const flush = (): void => {
        if (run.length >= 2) {
          wanted[`${t.key}#${index}`] = {
            path: run,
            // A point with no reading keeps the track's own colour rather than
            // being dropped: the phone was still there, we just do not know
            // what it measured.
            color: runColor ?? t.color
          };
          index += 1;
        }
        run = [];
      };

      pts.forEach((p) => {
        const color = colorAt(p, colorBy, t.color);
        if (started && color !== runColor && run.length > 0) {
          // Repeat the boundary point in both runs so the segments touch.
          const boundary = run[run.length - 1];
          flush();
          run = [boundary];
        }
        runColor = color;
        started = true;
        run.push({ lat: p.lat, lng: p.lng });
      });
      flush();
    });

    Object.keys(lines.current).forEach((key) => {
      if (!(key in wanted)) {
        lines.current[key].setMap(null);
        delete lines.current[key];
      }
    });

    const bounds = new window.google.maps.LatLngBounds();
    let any = false;
    Object.entries(wanted).forEach(([key, spec]) => {
      let line = lines.current[key];
      if (!line) {
        line = new window.google.maps.Polyline({
          map,
          strokeOpacity: 0.9,
          strokeWeight: 4
        });
        lines.current[key] = line;
      }
      line.setOptions({ strokeColor: spec.color });
      line.setPath(spec.path);
      spec.path.forEach((p) => {
        bounds.extend(p);
        any = true;
      });
    });
    if (any && !userMoved.current) {
      fitting.current = true;
      map.fitBounds(bounds, 48);
      window.setTimeout(() => {
        fitting.current = false;
      }, 300);
    }
  }, [map, tracks, version, colorBy]);

  useEffect(
    () => () => {
      Object.values(lines.current).forEach((l) => l.setMap(null));
      lines.current = {};
    },
    []
  );

  return (
    <>
      {tracks.map((t) => {
        const {points} = t;
        const start = points[0];
        const last = points[points.length - 1];
        if (!last) return null;
        // The newest reading of the coloured parameter. Only ~1 ping in 11
        // carries a GPS fix, so this is often an earlier point than the last
        // position — hence the search rather than just reading `last`.
        const reading = colorBy ? lastWithValue(points) : null;
        const readingValue = reading?.v;
        const dotColor =
          colorBy && typeof readingValue === 'number'
            ? colorAt(reading as TrackPoint, colorBy, t.color) ?? t.color
            : t.color;
        return (
          <div key={t.key}>
            {/* Where the track began, so A -> B has a readable direction. */}
            {start && points.length > 1 && (
              <AdvancedMarker position={{ lat: start.lat, lng: start.lng }} title="Start of track">
                <span className="block h-2.5 w-2.5 rounded-full border-2 border-white/80 bg-transparent shadow" />
              </AdvancedMarker>
            )}
            <AdvancedMarker position={{ lat: last.lat, lng: last.lng }} title={t.label}>
              <div className="flex -translate-y-1 flex-col items-center gap-1">
                {/* The reading itself, next to the position it was taken at —
                    the number is the point of the map, so it should not need a
                    hover to read. */}
                {colorBy && typeof readingValue === 'number' && (
                  <span
                    className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold shadow"
                    style={{
                      backgroundColor: dotColor,
                      borderColor: 'rgba(255,255,255,0.85)',
                      color: '#101828'
                    }}
                  >
                    {fmtScale(readingValue)}
                    {colorBy.unit ? ` ${colorBy.unit}` : ''}
                  </span>
                )}
                <span
                  className="block h-3.5 w-3.5 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: dotColor }}
                />
              </div>
            </AdvancedMarker>
          </div>
        );
      })}
    </>
  );
}

export default function LiveTrackMap({
  tracks,
  version,
  colorBy,
  colorOptions,
  colorKey,
  onColorChange
}: Props): ReactElement {
  const hasPoints = tracks.some((t) => t.points.length > 0);
  const latest = tracks.map((t) => lastWithValue(t.points)).find((p) => p) ?? null;
  const latestValue = latest?.v;

  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-gray-800 bg-container-500 p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-100">Phone location</h3>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">{scaleNote(colorBy)}</span>
          {onColorChange && (
            <label
              className="flex items-center gap-1.5 text-xs text-gray-400"
              htmlFor="ble-map-color"
            >
              Colour by
              <select
                id="ble-map-color"
                value={colorKey ?? ''}
                onChange={(e) => onColorChange(e.target.value)}
                className="rounded border border-gray-800 bg-background-700 px-2 py-1 text-xs text-gray-100 focus:border-primary-500 focus:outline-none"
              >
                <option value="">Flat line</option>
                {(colorOptions ?? []).map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      {/* The latest reading, repeated out of the map. The marker carries it
          too, but the marker can be scrolled off or sit under another. */}
      {colorBy && typeof latestValue === 'number' && (
        <div className="mb-2 flex items-baseline gap-2">
          <span className="text-xs text-gray-400">Latest {colorBy.label}</span>
          <span
            className="text-lg font-semibold"
            style={{ color: colorAt(latest as TrackPoint, colorBy, '#D0D5DD') ?? '#D0D5DD' }}
          >
            {fmtScale(latestValue)}
            {colorBy.unit ? ` ${colorBy.unit}` : ''}
          </span>
          {latest && (
            <span className="text-xs text-gray-500">
              at {new Date(latest.t).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      {/* Grows into whatever height the row gives this card instead of leaving
          a blank strip under a fixed-height map. The grid stretches both
          columns to the taller one — here the accelerometer and altitude stack
          — and this card's own content is shorter, more so when no legend is
          drawn. `min-h` keeps the old height when the card is on its own in a
          single column, where there is no spare room to take. */}
      <div className="relative min-h-[20rem] flex-1 overflow-hidden rounded-md">
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
          <Map
            mapId="2710c5f22fa0158b"
            defaultCenter={{ lat: 44.67, lng: -74.98 }}
            defaultZoom={14}
            gestureHandling="greedy"
            disableDefaultUI
            zoomControl
          >
            <Tracks tracks={tracks} version={version} colorBy={colorBy} />
          </Map>
        </APIProvider>
        {!hasPoints && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background-500/60 text-sm text-gray-400">
            No GPS position in this window yet
          </div>
        )}
      </div>

      {/* Legend only when colouring is on — a flat track has nothing to read. */}
      {colorBy?.mode === 'threshold' && hasPoints && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          {colorBy.bands.map((band) => (
            <span key={band.label} className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <span
                className="inline-block h-2 w-3 rounded-sm"
                style={{ backgroundColor: band.color }}
              />
              {band.label}
              <span className="text-gray-600">{band.detail}</span>
            </span>
          ))}
        </div>
      )}
      {colorBy?.mode === 'ramp' && hasPoints && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] text-gray-500">{fmtScale(colorBy.lo)}</span>
          <div className="flex h-2 flex-1 overflow-hidden rounded">
            {RAMP.map((c) => (
              <div key={c} className="flex-1" style={{ backgroundColor: c }} />
            ))}
          </div>
          <span className="text-[10px] text-gray-500">{fmtScale(colorBy.hi)}</span>
        </div>
      )}
      {colorBy?.mode === 'ramp' && hasPoints && (
        <p className="mt-1 text-[10px] text-gray-600">
          No threshold is configured for {colorBy.label}, so this scale is relative to the
          values in view — the colours cannot be compared across ranges.
        </p>
      )}
    </div>
  );
}
